
import React, { useEffect, useState, useCallback } from 'react';
import { X, Star, MapPin, Share2, ExternalLink, Check } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebaseService';
import { useTranslation } from 'react-i18next';
import { CampingSpot } from '../types';
import { APP_URL } from '../constants';

interface SharedReviewModalProps {
    reviewId: string;
    onClose: () => void;
    onGoToSpot: (spotId: string) => void;
}

const SharedReviewModal: React.FC<SharedReviewModalProps> = ({ reviewId, onClose, onGoToSpot }) => {
    const { t } = useTranslation();
    const [review, setReview] = useState<any>(null);
    const [spot, setSpot] = useState<CampingSpot | null>(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);

    const handleCopyLink = useCallback(() => {
        const url = `${APP_URL}/share/review/${reviewId}`;
        navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [reviewId]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const reviewDoc = await getDoc(doc(db, 'reviews', reviewId));
                if (reviewDoc.exists()) {
                    const rData = reviewDoc.data();
                    setReview(rData);

                    if (rData.spotId) {
                        const spotDoc = await getDoc(doc(db, 'spots', rData.spotId));
                        if (spotDoc.exists()) {
                            setSpot({ id: spotDoc.id, ...spotDoc.data() } as CampingSpot);
                        }
                    }
                }
            } catch (err) {
                console.error("Failed to load review", err);
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [reviewId]);

    if (!loading && !review) return null;

    return (
        <div className="fixed inset-0 z-[5000] flex items-end sm:items-center justify-center" style={{ top: 'env(safe-area-inset-top, 0px)' }}>
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-[var(--bg-surface)] w-full max-w-md rounded-t-3xl sm:rounded-3xl border border-[var(--border-secondary)] shadow-2xl overflow-hidden max-h-[90dvh] flex flex-col" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2.5 min-w-[44px] min-h-[44px] flex items-center justify-center bg-[var(--bg-input)] hover:bg-[var(--bg-input)] rounded-full text-white transition-colors z-10"
                >
                    <X size={20} />
                </button>

                {loading ? (
                    <div className="p-12 flex flex-col items-center justify-center gap-4 text-[var(--text-muted)]">
                        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                        <p className="text-xs uppercase font-black tracking-widest">{t('common.loading')}</p>
                    </div>
                ) : (
                    <div className="flex flex-col overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
                        {review.photoUrl && (
                            <div className="w-full h-64 relative">
                                <img src={review.photoUrl} alt="" className="w-full h-full object-cover" />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#1a1d1d] via-transparent to-transparent" />
                            </div>
                        )}

                        <div className={`p-6 ${review.photoUrl ? '-mt-12 relative' : ''} space-y-4`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    {spot && (
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded text-2xs font-black uppercase tracking-widest border border-emerald-500/30">
                                                {spot.name}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-[var(--bg-input)] border border-[var(--border-secondary)] overflow-hidden">
                                            {review.userPhoto ? (
                                                <img src={review.userPhoto} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)] font-bold">?</div>
                                            )}
                                        </div>
                                        <div>
                                            <p className="text-sm font-bold text-[var(--text-primary)]">{review.userName}</p>
                                            <div className="flex gap-0.5">
                                                {[1, 2, 3, 4, 5].map(s => (
                                                    <Star key={s} size={12} fill={s <= review.rating ? '#fbbf24' : 'none'} className={s <= review.rating ? 'text-amber-400' : 'text-[var(--text-muted)]'} />
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <span className="text-2xs font-bold text-[var(--text-muted)]">
                                    {new Date(review.createdAt).toLocaleDateString()}
                                </span>
                            </div>

                            <p className="text-sm text-[var(--text-secondary)] leading-relaxed bg-[var(--bg-surface-alt)] p-4 rounded-2xl border border-[var(--border-primary)]">
                                "{review.comment}"
                            </p>
                            {review.videoUrl && (
                                <a
                                    href={review.videoUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-tiny font-black uppercase tracking-wider text-sky-300 hover:text-sky-200"
                                >
                                    <ExternalLink size={14} />
                                    {t('reviews.watch_video')}
                                </a>
                            )}

                            <div className="pt-4 flex gap-3">
                                <button
                                    onClick={() => {
                                        if (spot) {
                                            onGoToSpot(spot.id);
                                            onClose();
                                        }
                                    }}
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-3 min-h-[44px] rounded-xl font-black text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                                >
                                    <MapPin size={16} /> {t('common.go_to_spot')}
                                </button>
                                <button
                                    onClick={handleCopyLink}
                                    className={`px-4 py-3 min-h-[44px] rounded-xl border transition-all flex items-center justify-center ${copied ? 'bg-emerald-500/20 border-emerald-500/30 text-emerald-400' : 'bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-primary)] border-[var(--border-primary)]'}`}
                                >
                                    {copied ? <Check size={18} /> : <Share2 size={18} />}
                                </button>
                            </div>

                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SharedReviewModal;
